import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-hot-toast";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "react-bootstrap";
import { isEmpty } from "lodash";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { getPlayableProperties, setDailyPlayableProperty } from "../../../services/PropertyService";
import { useUser } from "../../../context/UserContext";
import useCustomLoginWithRedirect from "../../../hooks/useCustomLoginWithRedirect";

import CustomButton from "../../../components/common/buttons/custom-button/custom-button";
import CustomInput from "../../../components/common/custom-input/custom-input";
import MoonLoader from "react-spinners/MoonLoader";

export default function SetPuzzleAdmin() {
    const { isAuthenticated, isLoading: authLoading } = useAuth0();
    const { userState } = useUser();
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const queryClient = useQueryClient();

    // Pagination state
    const [page, setPage] = useState(1);
    const limit = 20;

    // Get today's date in America/Chicago
    const chicagoTimeZone = "America/Chicago";
    const today = moment.tz(new Date(), chicagoTimeZone).format("YYYY-MM-DD");

    // Query playable properties
    const { data: playablePropertiesData, isLoading: isLoadingProperties } = useQuery({
        queryKey: ["admin-playable-properties", { min_play_date: today, page, limit }],
        queryFn: () => getPlayableProperties({ min_play_date: today, page, limit }),
        keepPreviousData: true,
    });

    // Mutation for setting daily property
    const setDailyPropertyMutation = useMutation({
        mutationFn: ({ play_date, mls_id, image_index, url }) =>
            setDailyPlayableProperty({
                play_date,
                ...(!isEmpty(mls_id) && { mls_id: `MRD${mls_id.trim()}` }),
                ...(!isEmpty(url) && { url }),
                image_index: image_index ? parseInt(image_index) : 1, // Default to 1
            }),
        onSuccess: () => {
            setShowSuccessModal(true);
            resetForm();
            // Invalidate all admin-playable-properties queries
            queryClient.invalidateQueries({
                queryKey: ["admin-playable-properties"],
            });
        },
        onError: (error) => {
            console.error("Error setting daily property:", error);
            toast.error(error.response?.data?.message || "Failed to set daily property");
        },
    });

    // Local state for table data
    const [playableProperties, setPlayableProperties] = useState([]);

    useEffect(() => {
        if (playablePropertiesData?.data?.data) {
            setPlayableProperties(playablePropertiesData.data.data);
        }
    }, [playablePropertiesData]);

    // Form state
    const [formData, setFormData] = useState({
        play_date: new Date(),
        mls_id: "",
        redfin_link: "", // Keep Redfin field but will be disabled
        image_index: "",
    });

    // Modal states
    const [showOverwriteModal, setShowOverwriteModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [existingProperty, setExistingProperty] = useState(null);

    // Form handlers
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetForm = () => {
        setFormData({
            play_date: new Date(),
            mls_id: "",
            redfin_link: "",
            image_index: "",
        });
    };

    // Pagination handlers
    const handleNextPage = () => {
        if (playablePropertiesData?.data?.hasNextPage) {
            setPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(prev => prev - 1);
        }
    };

    // Update handleSubmit to make image_index optional
    const handleSubmit = (e) => {
        // Prevent default form submission
        if (e) {
            e.preventDefault();
        }

        if (!formData.play_date) {
            toast.error("Please select a date");
            return;
        }

        if (isEmpty(formData.mls_id) && isEmpty(formData.redfin_link)) {
            toast.error("Please provide an MLS ID or Redfin Link");
            return;
        }

        // Check if property already exists for this date
        const existingProperty = playableProperties.find(
            (pp) => pp.play_date === moment(formData.play_date).format("YYYY-MM-DD")
        );

        if (existingProperty) {
            setExistingProperty({
                address:
                    existingProperty.Property?.full_address ||
                    existingProperty.Property?.street_address ||
                    "Unknown address",
                date: moment(formData.play_date).format("YYYY-MM-DD"),
            });
            setShowOverwriteModal(true);
            return;
        }

        // Use mutation to set daily property
        setDailyPropertyMutation.mutate({
            play_date: moment(formData.play_date).format("YYYY-MM-DD"),
            mls_id: formData.mls_id,
            url: formData.redfin_link,
            image_index: formData.image_index ? parseInt(formData.image_index) : 1, // Default to 1 if not provided
        });
    };

    const handleOverwrite = () => {
        setShowOverwriteModal(false);

        // Use mutation to overwrite daily property
        setDailyPropertyMutation.mutate({
            play_date: moment(formData.play_date).format("YYYY-MM-DD"),
            mls_id: formData.mls_id,
            url: formData.redfin_link,
            image_index: formData.image_index ? parseInt(formData.image_index) : 1, // Default to 1 if not provided
        });
    };

    const handleScheduleAnother = () => {
        setShowSuccessModal(false);
        resetForm();
    };

    // Update form validation to only require MLS ID and date
    const isFormValid = () => {
        return !!formData.play_date && (!!formData.mls_id || !!formData.redfin_link);
    };

    // Check if user has admin/editor privileges
    const hasAdminAccess =
        isAuthenticated &&
        userState?.type &&
        (userState.type === "admin" || userState.type === "editor");

    // Loading state
    if (authLoading) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
                <MoonLoader size={40} color="#63c19f" />
            </div>
        );
    }

    // Not authenticated or not admin/editor
    if (!hasAdminAccess) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-screen tw-p-8">
                <h2 className="tw-text-2xl tw-font-bold tw-mb-4">Access Denied</h2>
                <p className="tw-text-center tw-mb-4">
                    You must be an admin to access this page. Please{" "}
                    <button
                        onClick={() =>
                            customLoginWithRedirect({
                                authorizationParams: {
                                    redirect_uri: window.location.origin + "/admin/set-puzzle",
                                },
                                appState: {
                                    toggleProfile: true,
                                },
                            })
                        }
                        className="tw-text-blue-500 tw-underline tw-bg-transparent tw-border-none tw-cursor-pointer"
                    >
                        log in
                    </button>{" "}
                    to your admin account.
                </p>
            </div>
        );
    }

    return (
        <div className="tw-min-h-screen tw-bg-gray-50 tw-p-4">
            <div className="tw-max-w-6xl tw-mx-auto">
                <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8">PriceMe Puzzle Schedule</h1>

                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    {/* Properties Table */}
                    <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
                        <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Scheduled Properties</h2>

                        {isLoadingProperties ? (
                            <div className="tw-flex tw-justify-center tw-py-8">
                                <MoonLoader size={30} color="#63c19f" />
                            </div>
                        ) : (
                            <>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-sm">
                                        <thead>
                                            <tr className="tw-border-b tw-border-gray-200">
                                                <th className="tw-text-left tw-py-3 tw-px-2">Date</th>
                                                <th className="tw-text-left tw-py-3 tw-px-2">Property Address</th>
                                                <th className="tw-text-left tw-py-3 tw-px-2">Link</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {playableProperties.map((property) => (
                                                <tr key={property.id} className="tw-border-b tw-border-gray-100">
                                                    <td className="tw-py-3 tw-px-2">
                                                        {moment(property.play_date).format("MMM D, YYYY")}
                                                    </td>
                                                    <td className="tw-py-3 tw-px-2 tw-max-w-xs tw-truncate">
                                                        {property.Property?.full_address ||
                                                            property.Property?.street_address ||
                                                            "Unknown address"}
                                                    </td>
                                                    <td className="tw-py-3 tw-px-2">
                                                        <a
                                                            href={`/puzzle/${property.short_link}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="tw-text-blue-500 hover:tw-text-blue-700 tw-underline"
                                                        >
                                                            {property.short_link}
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                            {playableProperties.length === 0 && (
                                                <tr>
                                                    <td
                                                        colSpan="3"
                                                        className="tw-text-center tw-py-8 tw-text-gray-500"
                                                    >
                                                        No scheduled properties found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {playablePropertiesData?.data && (
                                    <div className="tw-flex tw-justify-between tw-items-center tw-mt-4 tw-px-2">
                                        <div className="tw-text-sm tw-text-gray-600">
                                            Page {page} of {Math.ceil(playablePropertiesData.data.total / limit)}
                                        </div>
                                        <div className="tw-flex tw-gap-2">
                                            <CustomButton
                                                className="tw-px-3 tw-py-1 tw-text-sm tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-700 tw-rounded"
                                                handleClick={handlePrevPage}
                                                text="Previous"
                                                disabled={page <= 1}
                                                dataCy="prev-page-btn"
                                                id="prev-page-btn"
                                            />
                                            <CustomButton
                                                className="tw-px-3 tw-py-1 tw-text-sm tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-700 tw-rounded"
                                                handleClick={handleNextPage}
                                                text="Next"
                                                disabled={!playablePropertiesData.data.hasNextPage}
                                                dataCy="next-page-btn"
                                                id="next-page-btn"
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Form */}
                    <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
                        <h2 className="tw-text-xl tw-font-semibold tw-mb-6">Set Daily Property</h2>

                        <form className="tw-space-y-6" onSubmit={handleSubmit}>
                            {/* Date Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Date
                                </label>
                                <DatePicker
                                    selected={formData.play_date}
                                    onChange={(date) => handleInputChange("play_date", date)}
                                    minDate={new Date()}
                                    dateFormat="MM/dd/yyyy"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    This property will be available at priceme.game/puzzle at 12am CT
                                </p>
                            </div>

                            {/* MLS ID Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    MLS ID
                                </label>
                                <CustomInput
                                    type="text"
                                    value={formData.mls_id}
                                    onChange={(e) => handleInputChange("mls_id", e.target.value)}
                                    placeholder="Enter MLS ID"
                                    disabled={!isEmpty(formData.redfin_link)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    tabIndex={1}
                                    onClear={() => handleInputChange("mls_id", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    For properties in or near Chicago
                                </p>
                            </div>

                            {/* Redfin Link Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Redfin Link
                                </label>
                                <CustomInput
                                    type="url"
                                    value={formData.redfin_link}
                                    onChange={(e) => handleInputChange("redfin_link", e.target.value)}
                                    placeholder="Enter Redfin URL"
                                    disabled={!isEmpty(formData.mls_id)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-bg-gray-100"
                                    tabIndex={2}
                                    onClear={() => handleInputChange("redfin_link", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    For properties NOT in or near Chicago
                                </p>
                            </div>

                            {/* Image Index Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Primary Image (Optional)
                                </label>
                                <CustomInput
                                    type="number"
                                    value={formData.image_index}
                                    onChange={(e) => handleInputChange("image_index", e.target.value)}
                                    placeholder="Enter image index"
                                    min="1"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    tabIndex={3}
                                    onClear={() => handleInputChange("image_index", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Enter a whole number that corresponds to best picture of this property
                                </p>
                            </div>

                            {/* Submit Button */}
                            <CustomButton
                                type="submit"
                                className="tw-w-full tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-py-2 tw-px-4 tw-rounded-md tw-transition-colors"
                                disabled={!isFormValid() || setDailyPropertyMutation.isPending}
                                text={setDailyPropertyMutation.isPending ? "Setting Property..." : "Submit"}
                                dataCy="submit-property-btn"
                                id="submit-property-btn"
                            />
                        </form>
                    </div>
                </div>
            </div>

            {/* Overwrite Confirmation Modal */}
            <Modal show={showOverwriteModal} onHide={() => setShowOverwriteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Overwrite</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {existingProperty?.address} is already the daily property for{" "}
                        {moment(formData.play_date).format("MMM D, YYYY")}. Are you sure you want to overwrite
                        this?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        className="tw-bg-gray-500 hover:tw-bg-gray-600 tw-text-white"
                        handleClick={() => setShowOverwriteModal(false)}
                        text="No"
                        disabled={setDailyPropertyMutation.isPending}
                        dataCy="overwrite-no-btn"
                        id="overwrite-no-btn"
                    />
                    <CustomButton
                        className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white"
                        handleClick={handleOverwrite}
                        text="Yes"
                        disabled={setDailyPropertyMutation.isPending}
                        dataCy="overwrite-yes-btn"
                        id="overwrite-yes-btn"
                    />
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Daily property was successfully set.</p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white"
                        handleClick={handleScheduleAnother}
                        text="Schedule Another Property"
                        dataCy="success-schedule-another-btn"
                        id="success-schedule-another-btn"
                    />
                </Modal.Footer>
            </Modal>
        </div>
    );
}
